import ReportLead, { useReportLoading } from '../shared/ReportLead';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx } from '../shared/report-design';
import { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { Alert, Avatar, Box, Button, Card, CardContent, Chip, LinearProgress, Skeleton, Stack, ThemeProvider, Typography } from '@mui/material';
import { alpha, getContrastRatio, useTheme } from '@mui/material/styles';
import { MdPersonOutline, MdSpeakerNotes } from 'react-icons/md';
import moment from 'moment';
import _ from 'lodash';
import { AppVarsContext } from '../../context/AppVarsContext';

const noteType = type => ({ global: 'General', meta: 'Meta', paid: 'Google Paid', grant: 'Google Grant', google: 'Google', stackadapt: 'Programmatic', infillion: 'Programmatic' }[type] || (type ? type.replace(/[_-]/g, ' ').replace(/^./, letter => letter.toUpperCase()) : 'General'));

const Comments = (props) => {
  const id = sessionStorage.getItem('rid');
  const brandTheme = useTheme();
  const theme = useMemo(() => brandTheme.palette.mode === 'dark' ? brandTheme : createReportSurfaceTheme(brandTheme), [brandTheme]);
  const accent = getContrastRatio(theme.palette.primary.main, theme.palette.background.paper) >= 4.5 ? theme.palette.primary.main : theme.palette.text.primary;
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  useReportLoading(loading, props.standalone ? props.setLoading : undefined);
  const { appVars, appLoading } = useContext(AppVarsContext);

  const fetchData = async () => {
    const request = ++requestId.current;
    setLoading(true);
    setError('');
    try {
    const sdate = sessionStorage.getItem('sDate');
    const edate = sessionStorage.getItem('eDate');

    const res = await fetch(`${appVars.api_url}/comments/get`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idi: id,
        sdate,
        edate,
        view: props.view
      })
    });

    if (!res.ok) throw new Error('Notes could not be loaded. Try another date range or refresh the page.');
    const response = await res.json();
    if (request !== requestId.current) return;

    const filtered = props.type !== 'All'
      ? _.filter(response, row => row.comment?.type === props.type)
      : response;
    setComments(Array.isArray(filtered) ? filtered : []);
    } catch (error) {
      if (request === requestId.current) setError(error.message);
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  };

  const latestFetch = useRef(fetchData);
  latestFetch.current = fetchData;
  useEffect(() => {

    if (!appLoading) {

      latestFetch.current(); // initial load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appLoading, appVars?.api_url]);

  useEffect(() => {
    const handleStorageChange = () => {
      // console.log('🟡 Triggered refresh by event');
       latestFetch.current();
     // setTrigger(prev => prev + 1); // triggers re-fetch
    };

    window.addEventListener('dc', handleStorageChange);
    const requests = requestId;
    return () => { ++requests.current; window.removeEventListener('dc', handleStorageChange); };
  }, []);

  return <ThemeProvider theme={theme}>
    <Box sx={props.standalone ? getReportPageSx(theme) : { width: '100%', minWidth: 0, display: 'grid', gap: 2 }}>
      {props.standalone && <Card component="section" elevation={0} sx={getReportSurfaceSx(theme)}><CardContent>
        <ReportLead eyebrow="Notes" title="Context behind the numbers" description="Updates and observations for this reporting period." />
      </CardContent></Card>}
      {loading && <LinearProgress aria-label="Loading notes" />}
      {error && <Alert severity="error" action={<Button color="inherit" onClick={fetchData} disabled={loading}>Retry</Button>}>{error}</Alert>}
      {loading && !comments.length ? <Stack spacing={2} aria-label="Loading note cards">
        {[0, 1, 2].map(index => <Card key={index} elevation={0} sx={getReportSurfaceSx(theme)}><CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}><Skeleton variant="circular" width={32} height={32} /><Skeleton width="35%" /></Stack>
          <Skeleton width="90%" /><Skeleton width="70%" />
        </CardContent></Card>)}
      </Stack> : comments.length ? <Box component="section" aria-label="Report notes" aria-busy={loading} sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        {comments.map((row, index) => <Card component="article" aria-label={`${noteType(row.comment?.type)} note`} key={row.id || row.comment?.id || `${row.createdAt}-${index}`} elevation={0} sx={{ ...getReportSurfaceSx(theme), borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.5)}` }}>
          <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
            <Avatar alt="Note author" src={row.comment?.owner ? `${appVars.api_url}/imageFiles/${row.comment.owner}.png` : undefined} sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: accent }}><MdPersonOutline /></Avatar>
            <Chip size="small" variant="outlined" label={noteType(row.comment?.type)} sx={{ color: accent, bgcolor: alpha(theme.palette.primary.main, 0.04), borderColor: alpha(theme.palette.primary.main, 0.2), fontWeight: 600 }} />
            <Typography component="time" dateTime={row.createdAt && moment(row.createdAt).isValid() ? moment(row.createdAt).toISOString() : undefined} variant="caption" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>{row.createdAt && moment(row.createdAt).isValid() ? moment(row.createdAt).format('MMM D, YYYY · h:mm A') : 'Date unavailable'}</Typography>
          </Box>
          <CardContent sx={{ color: 'text.primary', fontSize: '0.9375rem', lineHeight: 1.65, overflowWrap: 'anywhere', '& > :first-child': { mt: 0 }, '& > :last-child': { mb: 0 }, '& img, & video, & iframe': { maxWidth: '100%' }, '& img, & video': { height: 'auto' }, '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto' }, '& a': { color: accent, textDecoration: 'underline' }, '& pre': { maxWidth: '100%', overflowX: 'auto' } }} dangerouslySetInnerHTML={{ __html: row.comment?.comment || '' }} />
        </Card>)}
      </Box> : !loading && !error ? <Card elevation={0} sx={getReportSurfaceSx(theme)}><CardContent sx={{ py: 4, textAlign: 'center' }}>
        <MdSpeakerNotes size={28} color={accent} aria-hidden="true" />
        <Typography component="h2" variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>No notes for these dates</Typography>
        <Typography variant="body2" color="text.secondary">Choose another date range to see earlier updates.</Typography>
      </CardContent></Card> : null}
    </Box>
  </ThemeProvider>;
};

export default Comments;
